import { ClerkProviderWithRetry } from "@/web/auth/ClerkProviderWithRetry";
import { QueryClientProvider } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useCallback, useMemo, useRef, useState } from "react";
import { Toaster } from "sonner";
// oxlint-disable-next-line eslint/no-restricted-imports - useProcedure is exported only from spacetimedb/react, not spacetimedb/tanstack
import { SpacetimeDBProvider as SpacetimeDBProviderReact } from "spacetimedb/react";
import {
  SpacetimeDBProvider as SpacetimeDBProviderTanstack,
  type SpacetimeDBQueryClient,
} from "spacetimedb/tanstack";
import { PostHogProvider } from "@posthog/react";
import posthog from "posthog-js";

import { useClerkToken } from "@/web/auth/useClerkToken";
import { dismissToast, toaster } from "@/web/components/primitives/toast";
import { env } from "@/web/lib/env";
import { useMountEffect } from "@/web/lib/effects";
import type { RouterAppContext } from "@/web/router";
import { DbConnection, type ErrorContext } from "@tycho/web-bindings";

const HOST = env.VITE_SPACETIMEDB_HOST;
const DB_NAME = env.VITE_SPACETIMEDB_DB_NAME;

const DISCONNECTED_TOAST_ID = "disconnected-toast";
const SUBSCRIPTION_ERROR_TOAST_ID = "subscription-error-toast";
const DISCONNECTED_TOAST_DELAY_MS = 2_000;
const RECONNECT_BASE_DELAY_MS = 1_000;
const RECONNECT_MAX_DELAY_MS = 10_000;

/**
 * Strip the acquisition `invite` query param's value from a URL/path string so
 * the opaque link code never reaches analytics. Used by the PostHog
 * `before_send` hook on `$current_url` / `$pathname` / `$referrer`. Pure string
 * replacement (no URL parsing) so it also handles relative pathnames.
 */
function redactInviteParam(value: string): string {
  return value.replace(/([?&])invite=[^&]*/g, "$1invite=[REDACTED]");
}

/**
 * Distinguishes a transport failure (socket dropped, network blip, auth token
 * expiry) from a deterministic server-side error (a thrown view/reducer, e.g.
 * `smb_super_view` erroring on a dangling FK). The two demand opposite
 * responses: a transport failure should reconnect with backoff, but a server
 * error will throw again on the very next subscription, so reconnecting just
 * loops forever and leaves the user on a blank screen watching
 * `ws connection failed, trying again` in the console.
 *
 * We only treat an error as terminal when it carries a POSITIVE server-error
 * signature. Everything else — bare transport errors, and any unknown/opaque
 * message like Safari's `Load failed` — stays retryable. This asymmetry is
 * deliberate: wrongly retrying a fatal error costs a few doomed reconnects,
 * but wrongly bricking a transient failure strands the user until they refresh,
 * which is the exact regression we're guarding against.
 *
 * The signatures:
 *   - `[TAGGED_ERROR]` — the marker `Errors.taggedError` stamps on every
 *     server-thrown application error (see `spacetimedb/src/utils/errors.ts`).
 *     This is what appeared in the original stack trace
 *     (`[TAGGED_ERROR] Targetable property not found`).
 *   - `SenderError` — the other deliberate server-error class the reducers throw
 *     for user-facing validation failures.
 */
function isDeterministicServerError(err: Error): boolean {
  const message = err.message ?? "";
  return message.includes("[TAGGED_ERROR]") || message.includes("SenderError");
}

function makeHandlers({
  session,
  spacetimeDBQueryClient,
  isCurrentSession,
  showDisconnectedToast,
  dismissDisconnectedToast,
  onReconnectNeeded,
  onReconnectSucceeded,
  onFatalError,
}: {
  session: number;
  spacetimeDBQueryClient: SpacetimeDBQueryClient;
  isCurrentSession: (session: number) => boolean;
  showDisconnectedToast: () => void;
  dismissDisconnectedToast: () => void;
  onReconnectNeeded: (session: number) => void;
  onReconnectSucceeded: (session: number) => void;
  onFatalError: (session: number, err: Error) => void;
}) {
  // The query client owns subscription wiring for view-backed React Query data.
  // On connection loss we detach it from the dead socket; on reconnect we hand it
  // the fresh DbConnection so it can rehydrate subscriptions against that socket.
  const handleConnectionLoss = ({ detachQueryClient }: { detachQueryClient: boolean }) => {
    if (!isCurrentSession(session)) {
      return;
    }

    if (detachQueryClient) {
      spacetimeDBQueryClient.disconnect();
    }

    showDisconnectedToast();
    onReconnectNeeded(session);
  };

  // setConnection() hands the new live socket back to the SpacetimeDB query
  // client, which rehydrates any cached view subscriptions onto this connection.
  const onConnect = (conn: DbConnection) => {
    if (!isCurrentSession(session)) {
      return;
    }

    onReconnectSucceeded(session);
    dismissDisconnectedToast();
    spacetimeDBQueryClient.setConnection(conn);
  };

  const onDisconnect = () => {
    handleConnectionLoss({ detachQueryClient: true });
  };

  const onConnectError = (_ctx: ErrorContext, err: Error) => {
    if (!isCurrentSession(session)) {
      return;
    }

    // A deterministic server error (e.g. a thrown view) will re-throw on the
    // next subscription, so reconnecting can only loop. Stop retrying and hand
    // the user a real error state instead of an endless silent "reconnecting…".
    if (isDeterministicServerError(err)) {
      onFatalError(session, err);
      return;
    }

    handleConnectionLoss({ detachQueryClient: false });
  };

  return { onConnect, onDisconnect, onConnectError };
}

function SpacetimeDBProviders({ children }: { children: React.ReactNode }) {
  const token = useClerkToken();
  const router = useRouter();
  const { spacetimeDBQueryClient } = router.options.context as RouterAppContext;
  const [connectionSession, setConnectionSession] = useState(0);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const disconnectedToastTimeoutRef = useRef<number | null>(null);
  const isDisconnectedToastVisibleRef = useRef(false);
  const currentConnectionSessionRef = useRef(connectionSession);

  currentConnectionSessionRef.current = connectionSession;

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current === null) {
      return;
    }

    window.clearTimeout(reconnectTimeoutRef.current);
    reconnectTimeoutRef.current = null;
  }, []);

  const clearDisconnectedToastTimeout = useCallback(() => {
    if (disconnectedToastTimeoutRef.current === null) {
      return;
    }

    window.clearTimeout(disconnectedToastTimeoutRef.current);
    disconnectedToastTimeoutRef.current = null;
  }, []);

  const showDisconnectedToast = useCallback(() => {
    // Once the toast is pending or visible, keep it that way until a successful
    // reconnect dismisses it. Repeated connect errors should not restart the
    // delay window or keep re-firing the same toast forever.
    if (disconnectedToastTimeoutRef.current !== null || isDisconnectedToastVisibleRef.current) {
      return;
    }

    disconnectedToastTimeoutRef.current = window.setTimeout(() => {
      toaster({
        sonnerOptions: { duration: Infinity, id: DISCONNECTED_TOAST_ID },
        variant: "error",
        message: "Disconnected; attempting to reconnect…",
      });
      disconnectedToastTimeoutRef.current = null;
      isDisconnectedToastVisibleRef.current = true;
    }, DISCONNECTED_TOAST_DELAY_MS);
  }, []);

  const dismissDisconnectedToast = useCallback(() => {
    clearDisconnectedToastTimeout();
    isDisconnectedToastVisibleRef.current = false;
    dismissToast(DISCONNECTED_TOAST_ID);
  }, [clearDisconnectedToastTimeout]);

  const isCurrentSession = useCallback(
    (session: number) => currentConnectionSessionRef.current === session,
    [],
  );

  const scheduleReconnect = useCallback(
    (session: number) => {
      if (!isCurrentSession(session)) {
        return;
      }

      // Keep at most one retry timer in flight so transient disconnect/connect-error
      // pairs do not fan out into parallel reconnect attempts.
      if (reconnectTimeoutRef.current !== null) {
        return;
      }

      const nextAttempt = reconnectAttemptRef.current + 1;
      reconnectAttemptRef.current = nextAttempt;

      const delay = Math.min(
        RECONNECT_BASE_DELAY_MS * 2 ** (nextAttempt - 1),
        RECONNECT_MAX_DELAY_MS,
      );

      reconnectTimeoutRef.current = window.setTimeout(() => {
        reconnectTimeoutRef.current = null;
        setConnectionSession((currentSession) => currentSession + 1);
      }, delay);
    },
    [isCurrentSession],
  );

  const resetReconnectState = useCallback(
    (session: number) => {
      if (!isCurrentSession(session)) {
        return;
      }

      reconnectAttemptRef.current = 0;
      clearReconnectTimeout();
    },
    [clearReconnectTimeout, isCurrentSession],
  );

  // A deterministic server error (e.g. a thrown view) can't be healed by
  // reconnecting. Stop the retry loop, capture it for triage, and show the user
  // a distinct terminal error instead of a perpetual "reconnecting…" toast over
  // a blank screen.
  const handleFatalError = useCallback(
    (session: number, err: Error) => {
      if (!isCurrentSession(session)) {
        return;
      }

      clearReconnectTimeout();
      reconnectAttemptRef.current = 0;
      dismissDisconnectedToast();
      spacetimeDBQueryClient.disconnect();

      posthog.captureException(err, { source: "spacetimedb_subscription" });

      toaster({
        sonnerOptions: { duration: Infinity, id: SUBSCRIPTION_ERROR_TOAST_ID },
        variant: "error",
        message: "Something went wrong loading your data. Please refresh, or contact support.",
      });
    },
    [clearReconnectTimeout, dismissDisconnectedToast, isCurrentSession, spacetimeDBQueryClient],
  );

  useMountEffect(() => () => {
    clearReconnectTimeout();
    dismissDisconnectedToast();
    dismissToast(SUBSCRIPTION_ERROR_TOAST_ID);
  });

  // SpacetimeDB's ConnectionManager keys connections by builder URI + module name.
  // The SDK does not auto-reconnect a closed socket, so bump a fragment-only
  // session suffix to force a brand new managed connection without changing the
  // actual server endpoint that the websocket/fetch requests use.
  const connectionUri = useMemo(() => `${HOST}#session-${connectionSession}`, [connectionSession]);

  const connectionBuilder = useMemo(() => {
    const session = connectionSession;
    const { onConnect, onDisconnect, onConnectError } = makeHandlers({
      session,
      spacetimeDBQueryClient,
      isCurrentSession,
      showDisconnectedToast,
      dismissDisconnectedToast,
      onReconnectNeeded: scheduleReconnect,
      onReconnectSucceeded: resetReconnectState,
      onFatalError: handleFatalError,
    });

    return DbConnection.builder()
      .withUri(connectionUri)
      .withDatabaseName(DB_NAME)
      .withToken(token ?? undefined)
      .onConnect(onConnect)
      .onDisconnect(onDisconnect)
      .onConnectError(onConnectError);
  }, [
    connectionSession,
    connectionUri,
    dismissDisconnectedToast,
    handleFatalError,
    isCurrentSession,
    resetReconnectState,
    scheduleReconnect,
    showDisconnectedToast,
    spacetimeDBQueryClient,
    token,
  ]);

  return (
    <SpacetimeDBProviderTanstack connectionBuilder={connectionBuilder}>
      <SpacetimeDBProviderReact connectionBuilder={connectionBuilder}>
        {children}
      </SpacetimeDBProviderReact>
    </SpacetimeDBProviderTanstack>
  );
}

function QueryClientBridge({ children }: { children: React.ReactNode }) {
  // setupRouterSsrQueryIntegration owns dehydrate/hydrate but does not install a QueryClientProvider,
  // so useQueryClient() in hooks like useClerkToken needs this bridge.
  const router = useRouter();
  const { queryClient } = router.options.context as RouterAppContext;
  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}

export function ClientProviders({ children }: { children: React.ReactNode }) {
  useMountEffect(() => {
    if (env.VITE_TYCHO_ENV === "dev") {
      return;
    }
    posthog.init(env.VITE_POSTHOG_TOKEN, {
      api_host: "https://us.i.posthog.com",
      defaults: "2026-01-30",
      capture_exceptions: true,
      // Never send the opaque acquisition-invite code to analytics. The browser
      // URL carries `?invite=<code>` on the first pageview (before the route
      // strips it after a successful bind); redact it from every captured event
      // so the link credential never reaches PostHog.
      before_send: (event) => {
        if (!event) return event;
        const properties = event.properties ?? {};
        for (const key of ["$current_url", "$pathname", "$referrer"]) {
          if (typeof properties[key] === "string") {
            properties[key] = redactInviteParam(properties[key] as string);
          }
        }
        return {
          ...event,
          uuid: event.uuid ?? "",
          event: event.event ?? "",
          properties,
        };
      },
    });
  });

  return (
    <PostHogProvider client={posthog}>
      <QueryClientBridge>
        <ClerkProviderWithRetry publishableKey={env.VITE_CLERK_PUBLISHABLE_KEY}>
          <SpacetimeDBProviders>
            {children}
            <Toaster />
          </SpacetimeDBProviders>
        </ClerkProviderWithRetry>
      </QueryClientBridge>
    </PostHogProvider>
  );
}
